import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { formatarDataInteligente, formatarDataSemHora } from './utils/formatarData';
import API_CONFIG from './config/api';
import './EditarUsuario.css';

const EditarUsuario = ({ usuario, onClose, onSave, userLogado, onConfigurarPermissoes }) => {
  const [currentSection, setCurrentSection] = useState(0);
  const [formData, setFormData] = useState({
    nome: '',
    usuario: '',
    cargo: '',
    status: '',
    senha: '',
    whatsapp: '',
    notificacoes_whatsapp: false
  });
  const sections = [
    {
      title: 'Dados do Usuário',
      fields: ['nome', 'usuario', 'cargo']
    },
    {
      title: 'Segurança',
      fields: ['senha', 'status']
    },
    {
      title: 'WhatsApp',
      fields: ['whatsapp', 'notificacoes_whatsapp']
    }
  ];

  useEffect(() => {
    if (usuario) {
      
      setFormData({
        nome: usuario.nome || '',
        usuario: usuario.usuario || '',
        cargo: usuario.cargo || '',
        status: usuario.status || 'ativo',
        senha: '',
        whatsapp: usuario.informacoes_pessoais?.whatsapp || '',
        notificacoes_whatsapp: usuario.informacoes_pessoais?.notificacoes_whatsapp || false
      });

    }
  }, [usuario]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Formatar WhatsApp automaticamente
    if (name === 'whatsapp') {
      const apenasNumeros = value.replace(/\D/g, '');
      let valorFormatado = apenasNumeros;
      
      if (apenasNumeros.length <= 11) {
        // Formato: (00) 00000-0000 ou (00) 0000-0000
        if (apenasNumeros.length > 10) {
          valorFormatado = apenasNumeros.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
        } else if (apenasNumeros.length > 6) {
          valorFormatado = apenasNumeros.replace(/(\d{2})(\d{4})(\d{0,4})/, '($1) $2-$3');
        } else if (apenasNumeros.length > 2) {
          valorFormatado = apenasNumeros.replace(/(\d{2})(\d{0,5})/, '($1) $2');
        }
      }
      
      setFormData(prev => ({
        ...prev,
        [name]: valorFormatado
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const goToNextSection = () => {
    if (currentSection < sections.length - 1) {
      setCurrentSection(currentSection + 1);
    }
  };

  const goToPreviousSection = () => {
    if (currentSection > 0) {
      setCurrentSection(currentSection - 1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Enter nas etapas intermediárias também apenas avança, sem gravar o usuário.
    if (currentSection < sections.length - 1) {
      goToNextSection();
      return;
    }
    if (!podeEditar) return;

    // Validações
    if (!formData.nome || !formData.usuario || !formData.cargo) {
      if (onSave) onSave({ type: 'error', text: 'Preencha todos os campos obrigatórios' });
      return;
    }

    // Validar senha - obrigatória para novo usuário
    if (!usuario?._id && !formData.senha) {
      if (onSave) onSave({ type: 'error', text: 'A senha é obrigatória para novo usuário' });
      return;
    }

    // Validar senha se foi preenchida
    if (formData.senha && formData.senha.length < 6) {
      if (onSave) onSave({ type: 'error', text: 'A senha deve ter no mínimo 6 caracteres' });
      return;
    }

    try {
      const token = localStorage.getItem('token');
      // Acessos são gerenciados separadamente pelo analista, sem sobrescrever ações legadas.
      const dataToSend = { ...formData };
      
      // Se for super_admin criando novo usuário, adicionar loja_id do contexto
      if (userLogado.tipo === 'super_admin' && !usuario?._id) {
        const lojaContexto = localStorage.getItem('lojaContexto');
        if (lojaContexto) {
          try {
            const parsed = JSON.parse(lojaContexto);
            dataToSend.loja_id = parsed.id;
          } catch (e) {
            if (onSave) onSave({ type: 'error', text: 'Erro ao obter contexto da loja' });
            return;
          }
        } else {
          if (onSave) onSave({ type: 'error', text: 'Contexto de loja não encontrado. Por favor, selecione uma loja.' });
          return;
        }
      }
      
      // Sanitizar WhatsApp - remover formatação, manter apenas números
      if (dataToSend.whatsapp) {
        dataToSend.whatsapp = dataToSend.whatsapp.replace(/\D/g, '');
      }
      
      // Só enviar senha se foi preenchida (para edição) ou se é novo usuário
      if (!dataToSend.senha && usuario?._id) {
        delete dataToSend.senha;
      }

      let response;
      
      // Se tem _id, é edição (PUT), senão é criação (POST)
      if (usuario?._id) {
        response = await axios.put(
          `${API_CONFIG.API_URL}/usuarios/${usuario._id}`,
          dataToSend,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
      } else {
        response = await axios.post(
          `${API_CONFIG.API_URL}/usuarios`,
          dataToSend,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
      }

      if (response.data.success) {
        if (onSave) onSave({ 
          type: 'success', 
          text: usuario?._id ? 'Usuário atualizado com sucesso!' : 'Usuário criado com sucesso!' 
        });
        onClose();
      }
    } catch (error) {
      if (onSave) onSave({ type: 'error', text: error.response?.data?.message || 'Erro ao salvar usuário' });
    }
  };

  const handleDesativar = async () => {
    try {
      const token = localStorage.getItem('token');
      const novoStatus = usuario.status === 'ativo' ? 'inativo' : 'ativo';
      
      const response = await axios.put(
        `${API_CONFIG.API_URL}/usuarios/${usuario._id}`,
        { status: novoStatus },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        if (onSave) onSave({ type: 'success', text: `Usuário ${novoStatus === 'ativo' ? 'ativado' : 'desativado'} com sucesso!` });
        onClose();
      }
    } catch (error) {
      if (onSave) onSave({ type: 'error', text: error.response?.data?.message || 'Erro ao alterar status' });
    }
  };

  const handleExcluir = async () => {
    if (!usuario?._id) return;
    if (!confirm(`Tem certeza que deseja excluir o usuário "${usuario.nome}"? Esta ação é irreversível.`)) return;

    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(
        `${API_CONFIG.API_URL}/usuarios/${usuario._id}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.data && response.data.success) {
        if (onSave) onSave({ type: 'success', text: 'Usuário excluído com sucesso!' });
        onClose();
      } else {
        if (onSave) onSave({ type: 'error', text: response.data?.message || 'Erro ao excluir usuário' });
      }
    } catch (err) {
      if (onSave) onSave({ type: 'error', text: err.response?.data?.message || 'Erro ao excluir usuário' });
    }
  };

  const podeEditar = userLogado.cargo === 'Gerente' || userLogado.tipo === 'super_admin';
  const podeDesativar = (userLogado.cargo === 'Gerente' || userLogado.tipo === 'super_admin') && usuario._id !== userLogado.id;

  return (
    <div className="modal-overlay-preencher usuario-editor" style={{ padding: 0 }}>
      <div className="modal-preencher-container" style={{ width: '100%', height: '100%', maxWidth: '100%', maxHeight: '100%', borderRadius: 0, display: 'flex', flexDirection: 'column' }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-preencher-header">
          <h2>{usuario?._id ? 'Editar Usuário' : 'Novo Usuário'}</h2>
          {usuario?._id && userLogado.gerenciar_acessos && !['super_admin', 'bps'].includes(usuario.tipo) && onConfigurarPermissoes && (
            <button type="button" className="btn-secondary" onClick={() => onConfigurarPermissoes(usuario._id)}>Permissões de acesso</button>
          )}
          <button className="modal-close-btn" onClick={onClose}>×</button>
        </div>

        <div className="modal-section-indicator">
          <ol className="usuario-etapas" aria-label="Etapas do usuário">
            {sections.map((section, index) => (
              <li key={section.title} className={index === currentSection ? 'atual' : index < currentSection ? 'concluida' : ''} aria-current={index === currentSection ? 'step' : undefined}>
                <span className="usuario-etapa-numero">{index < currentSection ? '✓' : index + 1}</span>
                <span>{section.title}</span>
              </li>
            ))}
          </ol>
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${((currentSection + 1) / sections.length) * 100}%` }}></div>
          </div>
          <p className="section-counter">{currentSection + 1} de {sections.length} - {sections[currentSection].title}</p>
        </div>

        <form onSubmit={handleSubmit} className="preencher-form-new" style={{ maxHeight: 'none', flex: 1, display: 'flex', flexDirection: 'column' }}>
          <div className="form-sections" style={{ background: 'white', padding: '2rem', flex: 1, overflowY: 'auto' }}>
            {/* Seção 1: Dados do Usuário */}
            {currentSection === 0 && (
              <div className="form-section active">
                <h3 style={{ color: '#c14427', marginBottom: '1.5rem' }}>{sections[0].title}</h3>
                <div className="form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                  <div className="form-group">
                    <label>Nome Completo *</label>
                    <input
                      type="text"
                      name="nome"
                      value={formData.nome}
                      onChange={handleInputChange}
                      disabled={!podeEditar}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Nome de Usuário *</label>
                    <input
                      type="text"
                      name="usuario"
                      value={formData.usuario}
                      onChange={handleInputChange}
                      disabled={!podeEditar}
                      required
                      placeholder="nome.usuario"
                    />
                  </div>
                  <div className="form-group">
                    <label>Cargo *</label>
                    <select
                      name="cargo"
                      value={formData.cargo}
                      onChange={handleInputChange}
                      disabled={!podeEditar}
                      required
                    >
                      <option value="">Selecione...</option>
                      <option value="Gerente">Gerente</option>
                      <option value="Analista">Analista</option>
                      <option value="Financeiro">Financeiro</option>
                      <option value="Vendedor">Vendedor</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Seção 2: Segurança */}
            {currentSection === 1 && (
              <div className="form-section active">
                <h3 style={{ color: '#c14427', marginBottom: '1.5rem' }}>{sections[1].title}</h3>
                <div className="form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1.5rem' }}>
                  <div className="form-group">
                    <label>
                      {usuario?._id ? 'Nova Senha (deixe em branco para manter a atual)' : 'Senha *'}
                    </label>
                    <input
                      type="password"
                      name="senha"
                      value={formData.senha}
                      onChange={handleInputChange}
                      disabled={!podeEditar}
                      required={!usuario?._id}
                      placeholder={usuario?._id ? "Digite uma nova senha (mínimo 6 caracteres)" : "Digite a senha (mínimo 6 caracteres)"}
                      minLength={6}
                    />
                    {formData.senha && formData.senha.length < 6 && (
                      <small style={{color: '#e74c3c', fontSize: '0.85rem', marginTop: '0.25rem', display: 'block'}}>
                        A senha deve ter no mínimo 6 caracteres
                      </small>
                    )}
                  </div>
                  <div className="form-group">
                    <label>Status</label>
                    <input
                      type="text"
                      value={formData.status}
                      disabled
                      style={{background: '#f0f0f0', cursor: 'not-allowed'}}
                    />
                  </div>
                </div>

                {usuario?._id && (
                  <div className="usuario-info" style={{
                    marginTop: '2rem',
                    padding: '1.5rem',
                    background: '#f8f9fa',
                    borderRadius: '8px',
                    border: '1px solid #e0e0e0'
                  }}>
                    <h4 style={{ color: '#666', marginBottom: '1rem', fontSize: '0.95rem' }}>Informações Adicionais</h4>
                    <div className="info-row" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                      <strong style={{ color: '#555' }}>Último Login:</strong>
                      <span style={{ color: '#666' }}>
                        {usuario.ultimo_login ? 
                          formatarDataInteligente(usuario.ultimo_login) : 
                          'Nunca acessou'
                        }
                      </span>
                    </div>
                    <div className="info-row" style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <strong style={{ color: '#555' }}>Data de Cadastro:</strong>
                      <span style={{ color: '#666' }}>
                        {usuario.data_criacao ? 
                          formatarDataSemHora(usuario.data_criacao) : 
                          '-'
                        }
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Seção 3: WhatsApp */}
            {currentSection === 2 && (
              <div className="form-section active">
                <h3 style={{ color: '#c14427', marginBottom: '1.5rem' }}>{sections[2].title}</h3>
                <div className="form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1.5rem' }}>
                  <div className="form-group">
                    <label>Número do WhatsApp</label>
                    <input
                      type="text"
                      name="whatsapp"
                      value={formData.whatsapp}
                      onChange={handleInputChange}
                      disabled={!podeEditar}
                      placeholder="(00) 00000-0000"
                      maxLength={15}
                    />
                  </div>
                  <div className="form-group checkbox-group" style={{
                    padding: '1.5rem',
                    background: '#f8f9fa',
                    borderRadius: '8px',
                    border: '1px solid #e0e0e0'
                  }}>
                    <label style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem', display: 'block' }}>
                      Notificações via WhatsApp
                    </label>
                    <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <input
                        type="checkbox"
                        name="notificacoes_whatsapp"
                        checked={formData.notificacoes_whatsapp}
                        onChange={handleInputChange}
                        disabled={!podeEditar}
                      />
                      <span className="checkbox-custom"></span>
                      <span className="checkbox-text">
                        {formData.notificacoes_whatsapp ? 'Ativado' : 'Desativado'}
                      </span>
                    </label>
                    <small style={{ color: '#666', fontSize: '0.85rem', marginTop: '0.5rem', display: 'block' }}>
                      Receber notificações importantes pelo WhatsApp
                    </small>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Botões de Navegação */}
          <div className="modal-preencher-footer">
            <div style={{ display: 'flex', gap: '10px' }}>
              {podeDesativar && usuario?._id && (
                <>
                  <button 
                    type="button" 
                    onClick={handleDesativar}
                    style={{
                      padding: '12px 20px',
                      background: usuario.status === 'ativo' ? '#e74c3c' : '#27ae60',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '0.95rem',
                      fontWeight: '600'
                    }}
                  >
                    {usuario.status === 'ativo' ? '🚫 Desativar' : '✅ Ativar'}
                  </button>

                  {/* Excluir - apenas para gerentes e não para si mesmo */}
                  {podeDesativar && usuario._id !== userLogado.id && (
                    <button
                      type="button"
                      onClick={handleExcluir}
                      style={{
                        padding: '12px 20px',
                        background: '#b91c1c',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.95rem',
                        fontWeight: '600'
                      }}
                    >
                      🗑 Excluir Usuário
                    </button>
                  )}
                </>
              )}
            </div>
            
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
              <button type="button" className="btn-secondary" onClick={onClose}>
                Cancelar
              </button>

              {currentSection > 0 && (
                <button type="button" className="btn-secondary" onClick={goToPreviousSection}>
                  ← Anterior
                </button>
              )}

              {currentSection < sections.length - 1 && (
                <button type="button" className="btn-primary" onClick={goToNextSection}>
                  Prosseguir →
                </button>
              )}

              {podeEditar && currentSection === sections.length - 1 && (
                <button 
                  type="submit" 
                  className="btn-primary"
                  style={{ background: '#10b981', display: 'flex', alignItems: 'center', gap: '6px' }}
                >
                  {usuario?._id ? 'Salvar alterações' : 'Criar usuário'}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditarUsuario;
